"use client";

import { captureStorefrontEvent, getPostHogEventContext } from "@/lib/posthog-browser";
import { checkoutHref } from "@/lib/storefront/checkout-href";
import { publicStorePath } from "@/lib/storefront/public-url";
import { useEffect, useMemo, useRef, useState, useTransition, type RefObject } from "react";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, Truck, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetCloseButton } from "@/ui/components/ui/sheet";
import { useCart } from "./cart-context";
import { deleteCartLine, updateCartLineQuantity } from "./actions";
import { CartUpsell } from "./cart-upsell";
import { CartEssentials } from "./cart-essentials";
import { cn } from "@/lib/utils";
import { defaultLocale, formatPrice, isValidLocale, type Locale } from "@/config/locale";
import { localeConfig } from "@/config/locale";
import { hasDiscount } from "@/lib/pricing";
import { normalizeSaleorMediaUrl } from "@/lib/media-url";
import { CtaPill } from "@/ui/components/marketing/cta-pill";
import { SHIPPING_CONFIG, isMinimumOrderSatisfied, minimumOrderValueForChannel } from "@/config/shipping";
import { DhlShippingBadge } from "@/ui/components/shared/dhl-shipping-badge";
import type { AffiliatePromoCartHint } from "@/lib/affiliate/referral";
import type { ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";

interface CartLine {
	id: string;
	quantity: number;
	totalPrice: {
		gross: {
			amount: number;
			currency: string;
		};
	};
	variant: {
		id: string;
		name: string;
		saleUnit?: string | null;
		unitsPerSaleUnit?: string | null;
		product: {
			id: string;
			name: string;
			slug: string;
			category?: { name: string } | null;
			thumbnail?: {
				url: string;
				alt?: string | null;
			} | null;
		};
		pricing?: {
			price?: {
				gross: {
					amount: number;
					currency: string;
				};
			} | null;
			priceUndiscounted?: {
				gross: {
					amount: number;
					currency: string;
				};
			} | null;
		} | null;
		attributes?: Array<{
			attribute: {
				name?: string | null;
				slug?: string | null;
			};
			values: Array<{
				name?: string | null;
				value?: string | null;
			}>;
		}>;
	};
}

import { getColorHex, isColorAttribute } from "@/lib/colors";

interface VariantAttribute {
	name: string;
	value: string;
	colorHex?: string;
	isColor: boolean;
}

function getVariantDetails(variant: CartLine["variant"]): VariantAttribute[] {
	const attributes = variant.attributes || [];
	const result: VariantAttribute[] = [];

	for (const attr of attributes) {
		const slug = attr.attribute.slug || "";
		const name = attr.attribute.name || slug;
		const value = attr.values[0];

		if (!value?.name) continue;

		const isColor = isColorAttribute(slug);

		result.push({
			name,
			value: value.name,
			colorHex: isColor ? getColorHex(value) : undefined,
			isColor,
		});
	}

	// Sort: color first, then others
	return result.sort((a, b) => {
		if (a.isColor && !b.isColor) return -1;
		if (!a.isColor && b.isColor) return 1;
		return 0;
	});
}

type CartPricingResolved = {
	items: Array<{ id: string } & ResolvedMoneyPresentation>;
	totals: {
		subtotal: ResolvedMoneyPresentation;
		discount: ResolvedMoneyPresentation | null;
		total: ResolvedMoneyPresentation;
	};
	shippingThreshold: ResolvedMoneyPresentation;
	remainingToFreeShipping: ResolvedMoneyPresentation | null;
};

function lineResolvedPresentation(
	item: { id: string } & ResolvedMoneyPresentation,
): ResolvedMoneyPresentation {
	const { id: _id, ...rest } = item;
	return rest;
}

interface CartDrawerProps {
	savedCartCount?: number;
	checkoutId: string | null;
	lines: CartLine[];
	totalPrice: {
		gross: {
			amount: number;
			currency: string;
		};
	} | null;
	/** Line subtotal (pre–checkout-level voucher); optional for older callers. */
	subtotalPrice?: {
		gross: {
			amount: number;
			currency: string;
		};
	} | null;
	/** Checkout-level discount (e.g. affiliate voucher). */
	discount?: { amount: number; currency: string } | null;
	affiliatePromoHint?: AffiliatePromoCartHint | null;
	channel: string;
	reviewAggregates?: Record<string, { avgRating: number; count: number }>;
}

export function CartDrawer(props: CartDrawerProps) {
	const wasOpenRef = useRef(false);
	return <CartDrawerContent key={props.checkoutId} {...props} wasOpenRef={wasOpenRef} />;
}

function CartDrawerContent({
	savedCartCount = 0,
	checkoutId,
	lines,
	totalPrice,
	subtotalPrice,
	discount,
	affiliatePromoHint,
	channel,
	reviewAggregates = {},
	wasOpenRef,
}: CartDrawerProps & { wasOpenRef: RefObject<boolean> }) {
	const { isOpen, closeCart } = useCart();
	const [isPending, startTransition] = useTransition();
	const submitting = useRef(false);
	const [error, setError] = useState<"rejected" | "unconfirmed" | null>(null);
	const mutationDisabled = isPending || error === "unconfirmed";
	const { locale } = useParams<{ locale: string }>();
	const t = useTranslations("cart");
	const [pricingResolved, setPricingResolved] = useState<CartPricingResolved | null>(null);
	/** Avoid showing cached FX labels when the drawer is closed (no sync setState in effects). */
	const pricingResolvedForUi = isOpen ? pricingResolved : null;
	const loc = locale && isValidLocale(locale) ? (locale as Locale) : defaultLocale;
	const fmt = (amount: number, cur: string) => formatPrice(amount, cur, loc);

	const itemCount = lines.reduce((sum, line) => sum + line.quantity, 0);
	const subtotalBeforeDiscount =
		subtotalPrice?.gross.amount ?? lines.reduce((sum, line) => sum + line.totalPrice.gross.amount, 0);
	const currency =
		totalPrice?.gross.currency ?? subtotalPrice?.gross.currency ?? localeConfig.fallbackCurrency;
	const discountAmount = discount && discount.amount > 0 ? discount.amount : 0;
	const merchandiseSubtotal = subtotalBeforeDiscount;
	const displayTotal = totalPrice?.gross.amount ?? merchandiseSubtotal - discountAmount;

	useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			captureStorefrontEvent("cart_viewed", {
				checkout_id: checkoutId,
				channel,
				currency,
				total: displayTotal,
				items_count: itemCount,
				line_count: lines.length,
			});
		}
		wasOpenRef.current = isOpen;
	}, [isOpen, checkoutId, channel, currency, displayTotal, itemCount, lines.length, wasOpenRef]);

	const handleRemove = (lineId: string) => {
		if (!checkoutId) return;
		handleMutation(() => deleteCartLine(checkoutId, lineId, getPostHogEventContext()));
	};

	const handleUpdateQuantity = (lineId: string, newQuantity: number) => {
		if (!checkoutId || newQuantity < 1) return;
		handleMutation(() => updateCartLineQuantity(checkoutId, lineId, newQuantity, getPostHogEventContext()));
	};

	const handleMutation = (action: () => Promise<"applied" | "rejected" | "unconfirmed">) => {
		if (submitting.current || mutationDisabled) return;
		submitting.current = true;
		setError(null);
		startTransition(async () => {
			try {
				const result = await action();
				setError(result === "applied" ? null : result);
			} catch {
				setError("unconfirmed");
			} finally {
				submitting.current = false;
			}
		});
	};

	const threshold =
		SHIPPING_CONFIG.freeShippingThreshold[channel as keyof typeof SHIPPING_CONFIG.freeShippingThreshold] ??
		SHIPPING_CONFIG.freeShippingThreshold.eu;
	const progressToFreeShipping = Math.min((merchandiseSubtotal / threshold) * 100, 100);
	const amountToFreeShipping = Math.max(threshold - merchandiseSubtotal, 0);
	const minimumOrderValue = minimumOrderValueForChannel(channel);
	const meetsMinimumOrder = isMinimumOrderSatisfied(merchandiseSubtotal, channel);
	const amountToMinimumOrder = Math.max(minimumOrderValue - merchandiseSubtotal, 0);

	const pricingPayload = useMemo(() => {
		if (lines.length === 0) return null;
		return {
			locale,
			channel,
			country: null as string | null,
			items: lines.map((line) => ({
				id: line.id,
				amount: line.totalPrice.gross.amount,
				currency: line.totalPrice.gross.currency,
			})),
			totals: {
				subtotal: { amount: merchandiseSubtotal, currency },
				...(discountAmount > 0
					? { discount: { amount: discountAmount, currency: discount?.currency ?? currency } }
					: {}),
				total: { amount: displayTotal, currency },
			},
			shippingThreshold: { amount: threshold, currency },
			...(amountToFreeShipping > 0
				? { remainingToFreeShipping: { amount: amountToFreeShipping, currency } }
				: {}),
		};
	}, [
		lines,
		locale,
		channel,
		merchandiseSubtotal,
		currency,
		discountAmount,
		discount,
		displayTotal,
		threshold,
		amountToFreeShipping,
	]);

	useEffect(() => {
		if (!isOpen || !pricingPayload) return;

		let cancelled = false;
		(async () => {
			try {
				const res = await fetch("/api/pricing/resolve", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(pricingPayload),
				});
				if (!res.ok || cancelled) return;
				const data = (await res.json()) as CartPricingResolved;
				if (!cancelled) setPricingResolved(data);
			} catch {
				if (!cancelled) setPricingResolved(null);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [isOpen, pricingPayload]);

	return (
		<Sheet open={isOpen} onOpenChange={(open) => !open && closeCart()}>
			<SheetContent side="right" className="flex flex-col gap-0 overflow-hidden p-0">
				{/* Faint blueprint raster — datasheet feel */}
				<div
					aria-hidden
					className="pointer-events-none absolute inset-0 opacity-[0.04]"
					style={{
						backgroundImage:
							"linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)",
						backgroundSize: "32px 32px",
					}}
				/>
				{/* Header — datasheet style */}
				<SheetHeader className="relative justify-between border-b border-border bg-background px-5 py-4">
					<div className="flex flex-col items-start gap-1">
						<span className="flex items-center gap-2 font-mono text-[0.58rem] uppercase tracking-[0.22em] text-primary">
							<span className="relative flex h-1.5 w-1.5">
								<span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-success" />
							</span>
							Warenkorb · Live
						</span>
						<div className="flex items-baseline gap-2">
							<SheetTitle className="font-display text-[1.1rem] font-medium tracking-[-0.014em]">
								{t("yourBag")}
							</SheetTitle>
							<span className="font-mono text-[0.6rem] uppercase tabular-nums tracking-[0.18em] text-muted-foreground">
								{String(itemCount).padStart(2, "0")} · {t("items", { count: itemCount })}
							</span>
						</div>
					</div>
					<SheetCloseButton className="static" />
				</SheetHeader>
				{error && (
					<div role="alert" className="relative border-b border-border px-5 py-3 text-sm text-destructive">
						<p>{t(error === "rejected" ? "updateRejected" : "updateUnconfirmed")}</p>
						{error === "unconfirmed" && (
							<button type="button" className="mt-1 underline" onClick={() => window.location.reload()}>
								{t("reload")}
							</button>
						)}
					</div>
				)}

				{/* Minimum order precedes the later free-shipping progress. */}
				{lines.length > 0 && (
					<div className="bg-background/80 relative border-b border-border px-5 py-3" role="status">
						{affiliatePromoHint ? (
							<p className="mb-2 font-mono text-[0.58rem] uppercase tracking-[0.16em] text-accent">
								{affiliatePromoHint.kind === "PERCENTAGE"
									? t("affiliateDiscountPercentWillApply", { percent: affiliatePromoHint.value })
									: affiliatePromoHint.kind === "FIXED"
										? t("affiliateDiscountFixedWillApply", {
												amount: fmt(affiliatePromoHint.amount, affiliatePromoHint.currency),
											})
										: t("affiliateDiscountGenericWillApply")}
							</p>
						) : null}
						{/* Row 1: Icon + Text + Percentage */}
						<div className="flex items-center gap-2.5">
							<Truck
								className={cn(
									"h-4 w-4 shrink-0",
									meetsMinimumOrder && amountToFreeShipping <= 0 ? "text-success" : "text-primary-hero",
								)}
							/>
							{!meetsMinimumOrder ? (
								<span className="flex-1 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-foreground">
									{t("minimumOrderNotice", { amount: fmt(minimumOrderValue, currency) })} ·{" "}
									{t("minimumOrderRemaining", { amount: fmt(amountToMinimumOrder, currency) })}
								</span>
							) : amountToFreeShipping > 0 ? (
								<span className="flex-1 truncate font-mono text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-foreground">
									{t("freeShippingProgress", { amount: fmt(amountToFreeShipping, currency) })}
								</span>
							) : (
								<span className="flex-1 font-mono text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-success">
									{t("freeShippingQualified")}
								</span>
							)}
							{meetsMinimumOrder && (
								<span className="font-mono text-[0.6rem] font-semibold uppercase tabular-nums tracking-[0.14em] text-muted-foreground">
									{Math.round(progressToFreeShipping)}%
								</span>
							)}
						</div>
						{meetsMinimumOrder && (
							<div className="bg-primary/15 relative mt-2.5 h-2 w-full overflow-hidden rounded-full">
								<div
									className={cn(
										"h-full rounded-full shadow-sm transition-all duration-500 ease-out",
										amountToFreeShipping <= 0 ? "bg-success" : "bg-primary-hero",
									)}
									style={{ width: `${progressToFreeShipping}%` }}
								/>
							</div>
						)}
					</div>
				)}

				{savedCartCount > 0 && (
					<a
						href={publicStorePath(locale, "/cart")}
						onClick={closeCart}
						className="relative block border-b border-border px-5 py-3 text-sm font-medium underline"
					>
						{t("savedCartsTitle")}
					</a>
				)}
				{/* Scrollable area: cart items + upsell/essentials */}
				<div className="relative flex-1 overflow-y-auto">
					{lines.length === 0 ? (
						<div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
							<div className="relative w-full max-w-xs overflow-hidden rounded-2xl border border-border bg-background p-7 shadow-[0_20px_45px_-30px_rgba(8,39,74,0.2)]">
								{/* Inner raster */}
								<div
									aria-hidden
									className="pointer-events-none absolute inset-0 opacity-[0.04]"
									style={{
										backgroundImage:
											"linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)",
										backgroundSize: "20px 20px",
									}}
								/>
								{/* Corner crosshairs */}
								<svg
									aria-hidden
									viewBox="0 0 12 12"
									className="text-primary/30 pointer-events-none absolute left-2 top-2 h-2.5 w-2.5"
									fill="none"
									stroke="currentColor"
									strokeWidth="1"
								>
									<path d="M0 4 V0 H4" />
								</svg>
								<svg
									aria-hidden
									viewBox="0 0 12 12"
									className="text-primary/30 pointer-events-none absolute right-2 top-2 h-2.5 w-2.5 rotate-90"
									fill="none"
									stroke="currentColor"
									strokeWidth="1"
								>
									<path d="M0 4 V0 H4" />
								</svg>
								<svg
									aria-hidden
									viewBox="0 0 12 12"
									className="text-primary/30 pointer-events-none absolute bottom-2 left-2 h-2.5 w-2.5 -rotate-90"
									fill="none"
									stroke="currentColor"
									strokeWidth="1"
								>
									<path d="M0 4 V0 H4" />
								</svg>
								<svg
									aria-hidden
									viewBox="0 0 12 12"
									className="text-primary/30 pointer-events-none absolute bottom-2 right-2 h-2.5 w-2.5 rotate-180"
									fill="none"
									stroke="currentColor"
									strokeWidth="1"
								>
									<path d="M0 4 V0 H4" />
								</svg>

								<div className="bg-primary/[0.06] relative mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-border text-primary">
									<ShoppingBag className="h-6 w-6" aria-hidden />
								</div>
								<div className="relative font-mono text-[0.58rem] uppercase tracking-[0.22em] text-primary">
									Status · Leer
								</div>
								<h3 className="relative mt-2 font-display text-[1.1rem] font-medium leading-tight tracking-[-0.014em]">
									{t("empty")}
								</h3>
								<p className="relative mt-2 text-[0.86rem] leading-snug text-muted-foreground">
									{t("emptyDescription")}
								</p>
								<div className="relative mt-5 flex justify-center">
									<CtaPill
										href={`${publicStorePath(locale, "/peptides")}`}
										variant="primary"
										size="sm"
										onClick={closeCart}
									>
										{t("startShopping")}
									</CtaPill>
								</div>
							</div>
						</div>
					) : (
						<>
							<ul className="divide-y divide-border">
								{lines.map((line) => {
									const variantAttributes = getVariantDetails(line.variant);
									const isDiscounted = hasDiscount(
										line.variant.pricing?.price?.gross.amount,
										line.variant.pricing?.priceUndiscounted?.gross.amount,
									);
									const thumbnailUrl = normalizeSaleorMediaUrl(line.variant.product.thumbnail?.url);
									const linePricing = pricingResolvedForUi?.items?.find((i) => i.id === line.id);

									return (
										<li key={line.id} className="px-5 py-3">
											<div className="flex gap-3">
												{/* Product Image — compact */}
												<Link
													href={`${publicStorePath(locale, `/products/${line.variant.product.slug}?variant=${line.variant.id}`)}`}
													onClick={closeCart}
													className="group relative h-16 w-12 shrink-0 overflow-hidden rounded-md bg-secondary"
												>
													{thumbnailUrl ? (
														<Image
															src={thumbnailUrl}
															alt={line.variant.product.thumbnail?.alt ?? line.variant.product.name}
															fill
															sizes="48px"
															className="group-hover:scale-105 object-cover transition-transform duration-300"
														/>
													) : (
														<div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
															<ShoppingBag className="h-4 w-4" />
														</div>
													)}
												</Link>

												{/* Product Details */}
												<div className="min-w-0 flex-1">
													<div className="flex items-start justify-between gap-1.5">
														<div className="min-w-0">
															<Link
																href={`${publicStorePath(locale, `/products/${line.variant.product.slug}?variant=${line.variant.id}`)}`}
																onClick={closeCart}
																className="line-clamp-1 text-sm font-medium leading-tight hover:underline"
															>
																{line.variant.product.name}
															</Link>
															{/* Review stars */}
															{(() => {
																const review = reviewAggregates[line.variant.product.slug];
																if (!review || review.count === 0) return null;
																return (
																	<div className="mt-0.5 flex items-center gap-1">
																		<svg
																			className="h-2.5 w-2.5 fill-amber-400 text-amber-400"
																			viewBox="0 0 20 20"
																			aria-hidden="true"
																		>
																			<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
																		</svg>
																		<span className="text-[10px] tabular-nums text-muted-foreground">
																			{review.avgRating.toFixed(1)}
																		</span>
																	</div>
																);
															})()}
															{/* Variant attributes */}
															{variantAttributes.length > 0 ? (
																<div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
																	{variantAttributes.map((attr, index) => (
																		<span key={attr.name} className="flex items-center gap-1">
																			{index > 0 && <span className="text-border">|</span>}
																			{attr.colorHex && (
																				<span
																					className="h-2.5 w-2.5 rounded-full border border-border"
																					style={{ backgroundColor: attr.colorHex }}
																				/>
																			)}
																			<span>{attr.isColor ? attr.value : `${attr.name} ${attr.value}`}</span>
																		</span>
																	))}
																</div>
															) : line.variant.name && line.variant.name !== line.variant.id ? (
																<p className="mt-0.5 text-[11px] text-muted-foreground">
																	{line.variant.name}
																</p>
															) : null}
														</div>
														<button
															type="button"
															onClick={() => handleRemove(line.id)}
															disabled={mutationDisabled}
															className="text-muted-foreground/60 -mr-1 -mt-0.5 shrink-0 rounded p-1 transition-colors hover:text-destructive"
														>
															<Trash2 className="h-3.5 w-3.5" />
															<span className="sr-only">
																{t("remove", { product: line.variant.product.name })}
															</span>
														</button>
													</div>

													{/* Quantity & Price — tighter */}
													<div className="mt-2 flex items-center justify-between">
														{/* Quantity Selector — 44px tap targets on mobile */}
														<div className="flex items-center rounded-full border border-border">
															<button
																type="button"
																onClick={() => handleUpdateQuantity(line.id, line.quantity - 1)}
																disabled={line.quantity <= 1 || mutationDisabled}
																className="flex h-10 w-10 items-center justify-center transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40 sm:h-8 sm:w-8"
															>
																<Minus className="h-3.5 w-3.5" />
																<span className="sr-only">{t("decreaseQuantity")}</span>
															</button>
															<span className="min-w-[1.5rem] text-center text-sm font-medium tabular-nums sm:text-xs">
																{line.quantity}
															</span>
															<button
																type="button"
																onClick={() => handleUpdateQuantity(line.id, line.quantity + 1)}
																disabled={mutationDisabled}
																className="flex h-10 w-10 items-center justify-center transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40 sm:h-8 sm:w-8"
															>
																<Plus className="h-3.5 w-3.5" />
																<span className="sr-only">{t("increaseQuantity")}</span>
															</button>
														</div>

														{/* Price */}
														<div className="text-right">
															<span className="text-sm font-medium">
																{linePricing ? (
																	<ReferencePrimaryPrice
																		presentation={lineResolvedPresentation(linePricing)}
																		locale={locale}
																		channel={channel}
																		size="sm"
																		showReferenceTooltip={false}
																	/>
																) : (
																	fmt(line.totalPrice.gross.amount, line.totalPrice.gross.currency)
																)}
															</span>
															{isDiscounted && line.variant.pricing?.priceUndiscounted && (
																<span className="block text-[11px] text-muted-foreground line-through">
																	{fmt(
																		line.variant.pricing.priceUndiscounted.gross.amount * line.quantity,
																		line.variant.pricing.priceUndiscounted.gross.currency,
																	)}
																</span>
															)}
														</div>
													</div>
												</div>
											</div>
										</li>
									);
								})}
							</ul>

							{/* Upsell & Essentials — inside scroll area, after items */}
							{checkoutId && (
								<CartUpsell
									lines={lines.map((line) => ({
										id: line.id,
										variantId: line.variant.id,
										variantName: line.variant.name,
										saleUnit: line.variant.saleUnit,
										productSlug: line.variant.product.slug,
										productName: line.variant.product.name,
										thumbnailUrl: line.variant.product.thumbnail?.url ?? undefined,
										totalPrice: line.totalPrice.gross.amount,
									}))}
									subtotal={merchandiseSubtotal}
									checkoutId={checkoutId}
									channel={channel}
									locale={locale}
								/>
							)}
							{checkoutId && (
								<CartEssentials
									lines={lines.map((line) => ({
										productSlug: line.variant.product.slug,
										categoryName: line.variant.product.category?.name ?? null,
									}))}
									checkoutId={checkoutId}
									channel={channel}
									locale={locale}
								/>
							)}
						</>
					)}
				</div>

				{/* Footer — datasheet style */}
				{lines.length > 0 && (
					<div className="relative border-t border-border bg-background">
						{/* Order Summary */}
						<div className="border-border/70 relative space-y-2 border-b px-5 py-4">
							<div className="mb-2 flex items-center justify-between font-mono text-[0.55rem] uppercase tracking-[0.2em] text-primary">
								<span>Zusammenfassung · Live</span>
								<span className="text-muted-foreground">Doc-ID · CART-2026.05</span>
							</div>
							<div className="flex items-center justify-between text-xs">
								<span className="font-mono uppercase tracking-[0.14em] text-muted-foreground">
									{t("subtotal")}
								</span>
								<span>
									{pricingResolvedForUi ? (
										<ReferencePrimaryPrice
											presentation={pricingResolvedForUi.totals.subtotal}
											locale={locale}
											channel={channel}
											size="sm"
											showReferenceTooltip={false}
										/>
									) : (
										fmt(merchandiseSubtotal, currency)
									)}
								</span>
							</div>
							{discountAmount > 0 ? (
								<div className="flex items-center justify-between text-xs text-green-600">
									<span className="font-mono uppercase tracking-[0.14em]">{t("discount")}</span>
									<span className="inline-flex items-center gap-0.5">
										-
										{pricingResolvedForUi?.totals.discount ? (
											<ReferencePrimaryPrice
												presentation={pricingResolvedForUi.totals.discount}
												locale={locale}
												channel={channel}
												size="sm"
												className="text-green-600"
											/>
										) : (
											fmt(discountAmount, discount?.currency ?? currency)
										)}
									</span>
								</div>
							) : null}
							<div className="flex items-center justify-between text-xs">
								<span className="font-mono uppercase tracking-[0.14em] text-muted-foreground">
									{t("shipping")}
								</span>
								<span>{merchandiseSubtotal >= threshold ? t("shippingFree") : t("shippingCalculated")}</span>
							</div>
							<div className="mt-1 flex items-center justify-between border-t border-border pt-3">
								<span className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-primary">
									{t("total")}
								</span>
								<span>
									{pricingResolved ? (
										<ReferencePrimaryPrice
											presentation={pricingResolved.totals.total}
											locale={locale}
											channel={channel}
											size="md"
										/>
									) : (
										fmt(displayTotal, currency)
									)}
								</span>
							</div>
						</div>

						{/* Actions — pill-shaped, datasheet style */}
						<div className="space-y-2 px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">
							{/* Full document navigation: avoids soft-nav DOM/hydration glitches (PDP + checkout + ssr:false Root). */}
							{meetsMinimumOrder && !mutationDisabled ? (
								<a
									href={checkoutHref(locale, checkoutId!)}
									onClick={(event) => {
										if (submitting.current || mutationDisabled) {
											event.preventDefault();
											return;
										}
										closeCart();
									}}
									className="group/cta inline-flex w-full items-center justify-between gap-3 rounded-full bg-primary px-6 py-3.5 font-mono text-[0.7rem] font-medium uppercase tracking-[0.18em] text-primary-foreground shadow-md transition-colors hover:bg-primary-dark"
								>
									<span>{t("checkout")}</span>
									<ArrowRight
										className="group-hover/cta:translate-x-1 h-4 w-4 shrink-0 transition-transform"
										aria-hidden
									/>
								</a>
							) : (
								<span
									aria-disabled="true"
									className="inline-flex w-full cursor-not-allowed items-center justify-between gap-3 rounded-full bg-muted px-6 py-3.5 font-mono text-[0.7rem] font-medium uppercase tracking-[0.18em] text-muted-foreground"
								>
									<span>{t("checkout")}</span>
									<ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
								</span>
							)}
							<Link
								href={`${publicStorePath(locale, "/peptides")}`}
								onClick={closeCart}
								className="hover:border-primary/40 hover:bg-primary/[0.04] inline-flex w-full items-center justify-center gap-2 rounded-full border border-border bg-background px-6 py-3 font-mono text-[0.65rem] font-medium uppercase tracking-[0.16em] text-foreground transition-colors hover:text-primary"
							>
								{t("continueShopping")}
							</Link>
						</div>

						<div className="flex items-center justify-center gap-x-4 border-t border-border px-5 py-2.5 text-[11px] text-muted-foreground">
							<span className="flex items-center gap-1">
								<Truck className="h-3.5 w-3.5" />
								{t("freeDeliveryOver", { amount: fmt(threshold, currency) })}
							</span>
							<span className="flex items-center gap-1">
								<ShieldCheck className="h-3.5 w-3.5" />
								{t("returns")}
							</span>
							{!channel.toLowerCase().includes("us") && (
								<DhlShippingBadge label={t("shippedViaDhl")} variant="inline" size="sm" />
							)}
						</div>
					</div>
				)}
			</SheetContent>
		</Sheet>
	);
}
